/**
 * English mirror of ./strings.ar.js — GENERATED by scripts/i18n/build.mjs.
 *
 * Wording follows the legacy Locale/*.resx English resources wherever the
 * legacy screen had the same label. To change any of it, edit
 * scripts/i18n/strings.en.json and re-run the script.
 */

export const strings = {
  actionDone: 'Operation Completed Successfully',
  actionFailed: 'The operation could not be completed, please try again',
  fail: {
    action: 'Could not complete the operation on this user',
    directory: 'Could not import the users from the directory',
  },
  title: 'Users List',
  subtitle: 'Select a user to update his profile data',
  nameNeedsLetters: 'The name must contain letters.',

  tabs: {
    ACTIVE: 'Active',
    SUSPENDED: 'Suspended & Expired',
    WAITING_ACTIVATION_APPROVAL: 'Waiting Activation',
    IDEAL_USERS: 'Inactive Users',
  },

  columns: {
    index: 'ID',
    userName: 'Username',
    name: 'Full name',
    email: 'Email',
    groups: 'Group',
    status: 'Status',
  },

  actions: {
    add: 'Add',
    createLocalUser: 'Create User (Local)',
    importFromExcelLocal: 'Import From Excel (Local)',
    importFromExcelAd: 'Import From Excel (Active Directory)',
    update: 'Update',
    syncDataSource: 'Fetch and synchronize data',
    importResigned: 'Update the data of resigned employees from an Excel',
    resetPasswords: 'Reset Passwords For Users',
    filter: 'Search',
  },

  clone: {
    title: 'Pull users from the integration source',
    hint: 'Add the users found in the integration source to the users list',
    source: 'Integration source',
    sourcePlaceholder: 'Choose the integration source',
    note: 'This uses the user integration mode in Data sync settings. Import adds new accounts, Sync updates existing directory accounts, and Import and sync does both. The source resignation settings also apply when syncing.',
    submit: 'Pull users',
    running: 'Please wait...',
  },

  fetch: {
    action: 'Fetch from the integration source',
    hint: 'Type the username, then fetch their details from the integration source to fill the fields',
    running: 'Fetching...',
    usernameRequired: 'Type the username first',
    filled: "The user's details were fetched",
    notFound: 'That user was not found in the integration source',
  },

  filter: {
    groupLabel: 'Departments:',
    groupPlaceholder: 'Group',
    typeLabel: 'Type:',
    typePlaceholder: 'User type',
    adminsOnly: 'Admins Only',
    usersOnly: 'Users Only',
    cancel: 'Cancel',
    submit: 'Search',
  },

  row: {
    edit: 'Edit User Data',
    status: 'Account status',
    delete: 'Delete user',
    deleteConfirm: (name) =>
      `${name}'s account will be permanently deleted, with its roles and sign-in history. This cannot be undone.`,
  },

  footer: {
    deleteInactive: 'Delete',
  },

  create: {
    title: 'Create User (Local)',
    hint: 'This feature is used to register a local user without integration with Active Directory',
    username: 'Username',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    email: 'Email',
    submit: 'Create User',
    defaultDepartment: 'Default group',
    errors: {
      // Users_EmptyUsername, Users_WhiteSpacesUsername, Users_UsernameTooLong, Users_InvalidUsername
      usernameRequired: 'Username is required',
      usernameSpaces: 'Username cannot contain spaces',
      usernameTooLong: (max) => `The username must not exceed ${max} characters`,
      usernameChars: 'The username may contain English letters, digits and - . _ @ + only',
      passwordRequired: 'Password is required.',
      passwordFormat:
        'The password must be 8 to 64 characters with an uppercase letter, a lowercase letter, a digit and a symbol, and no spaces',
      confirmRequired: 'Confirm Password is required.',
      confirmMismatch: 'The Password and Confirmation Password must match.',
      emailRequired: 'E-mail is required.',
      emailFormat: 'Please enter a valid e-mail address.',
      arabicNameRequired: 'Arabic Name is required.',
      englishNameRequired: 'English Name is required.',
    },
  },

  importModal: {
    intro: 'Please upload a file in Excel (.xlsx) or CSV (.csv) format',
    columnsIntro: 'The file must contain the following columns',
    mandatory: 'Mandatory',
    optional: 'Optional',
    notesTitle: 'Important Notes',
    notes:
      'The first row in the file must contain the column names, exactly matching the names and order shown in the table',
    template: 'Download template',
    save: 'Save',
    fileError: 'Excel or CSV file',
  },

  importTypes: {
    UsersByEmail: {
      title: 'Import From Excel (Local)',
      hint: 'This feature is used to register multiple local users through an Excel file without integration with Active Directory',
      columns: [
        { name: 'Email', required: true },
        { name: 'FullName', required: false },
        { name: 'Department', required: false },
      ],
    },
    ActiveDirectoryUsers: {
      title: 'Import From Excel (Active Directory)',
      hint: 'Upload a file containing only the Username column. Details and departments are fetched from the selected LDAP source using its field mappings. Existing accounts stay unchanged and do not consume additional seats.',
      columns: [{ name: 'Username', required: true }],
    },
    ResignedUsers: {
      title: 'Update the data of resigned employees from an Excel',
      hint: 'This feature is used to manually update resignations (used only if synchronization is not enabled and resignations detection from the integration source is not activated)',
      columns: [
        { name: 'Username', required: true },
        { name: 'Resignation Date (d/M/yyyy)', required: true },
      ],
    },
    ResetUsersPasswords: {
      title: 'Reset Passwords For Users',
      hint: 'This can be used to reset passwords for existing users',
      columns: [{ name: 'Username', required: true }],
    },
  },

  importRejected: {
    title: 'Rows that were not imported',
    lead: 'The remaining rows were imported.',
    hint: 'Check these rows in the file and correct them, then import again, or export them to Excel to review.',
    search: 'Search the rows',
    count: (shown, total) => `${shown} of ${total}`,
    empty: 'No row matches the search',

    nameHeader: 'Value',
    fileName: (type) => `Rows that were not imported - ${type}`,

    sheetName: 'Rejected rows',
    exportFailed: 'The file could not be exported',
  },

  edit: {
    title: 'Edit User Data',
    tabs: {
      profile: 'Edit User Data',
      permissions: 'Privileges',
      password: 'Change Password',
    },
    nameAr: 'Arabic Name',
    nameEn: 'English Name',
    group: 'Group',
    gender: 'Gender',
    selectGender: 'Select Gender',
    male: 'Male',
    female: 'Female',
    email: 'Email',
    phone: 'Telephone',
    mobile: 'Mobile',
    employeeId: 'Employee Id',
    save: 'Save',
    required: 'Field required',
    nameLetters: 'The name must contain letters, not only symbols or digits',
    lengthError: 'Text must be with in 3 to 200 letters.',
    phoneFormat: 'Enter a valid number: digits only, optionally starting with +',
    selectAllRoles: 'Select All',
    updateRoles: 'Update',
    roleUser: 'Normal User',
    roleAdmin: 'Admin',
    autoPasswordHint: 'Change the password automatically and send it to the user\'s email',
    autoPasswordButton: 'Change user password',
    manualPasswordHint: 'Set the password manually',
    newPassword: 'Password',
    confirmNewPassword: 'Confirm Password',
    changePassword: 'Change Password',
    passwordMismatch: 'Password and confirm password not matched',

    noPasswordChange: 'Cannot change password',
    noPasswordChangeReason: 'Cannot change password when login is by Active directory',
  },

  status: {
    tabs: {
      approve: 'Account Status',
      expiry: 'Account Expiry',
      locked: 'User Temporary Locking',
      resigned: 'Resignation',
    },
    approved: 'Is Approved',
    notes: 'Notes',
    update: 'Update',
    save: 'Save',
    expiryLabel: 'Account Expiry',
    lockoutLabel: 'Lockout End Date',
    lockoutHint: 'Leave empty to unlock the account',
    unlockNow: 'Unlock now',
    lockedUntil: 'Locked until',
    notLocked: 'This account is not locked.',
    fetchedResignation: 'Fetched Resignation Date By Sync Operation',
    none: 'None',
    resignationLabel: 'Resignation Date',
    resignationHint: 'Leave empty to cancel the resignation',
  },

  deleteInactiveConfirm: (count) =>
    `The accounts shown on this page (${count}) will be deleted permanently. This cannot be undone.`,
  selectAll: 'Select All',
  close: 'Close',
}
